import { BrowserActivityType, ElementType, Track } from '@1debit/analytics';
import { UnitType } from '@1debit/chime-schemas/typescript_web_analytics';
import { trackEvent } from 'libs/analytics';
import { TocAnchorClickedEvent, TocToggleClickedEvent, ScrollDepthProps } from './types';

// PreEnrollment NonGA4 Events

export interface OutboundLinkTappedEvent {
  label: string;
  item_value: string;
  link_type: string;
  module: {
    module_title: string;
    module_name: string;
    module_index: string;
    module_type: string;
  };
}

export interface ExperimentBucketedEvent {
  unit_id: string;
  unit_type: UnitType;
  experiment_key: string;
  variant_key: string;
}

/**
 * Tracks a Statsig experiment exposure via the event-router. The name and
 * schema mirror chime.memberexperience.v1.ExperimentBucketedEvent.
 */
export function trackExperimentBucketed(event: ExperimentBucketedEvent): void {
  trackEvent(Track.WebActionTriggered, {
    unique_id: 'experiment_bucketed',
    item_details: {
      experiment_bucketed: {
        unit_id: event.unit_id,
        unit_type: event.unit_type,
        experiment_key: event.experiment_key,
        variant_key: event.variant_key,
      },
    },
  });
}

/**
 * Tracks when a user taps an outbound link
 * @param event - The outbound link event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED in types.ts
 */
export function trackOutboundLinkTapped(event: OutboundLinkTappedEvent): void {
  const unique_id = 'outbound_link_tapped';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_LINK,
    item_details: {
      [unique_id]: {
        link_type: event.link_type,
        module: event.module,
      },
    },
  });
}

export interface ModuleElementTappedEvent {
  label: string;
  item_value: string;
  module: {
    module_title: string;
    module_name: string;
    module_index: string;
    module_type: string;
  };
}

/**
 * Tracks when a user taps an element within a module
 * @param event - The module element event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED in types.ts
 */
export function trackModuleElementTapped(event: ModuleElementTappedEvent): void {
  const unique_id = 'module_element_tapped';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    item_details: {
      [unique_id]: {
        module: event.module,
      },
    },
  });
}

export interface NavigationBarInteractionEvent {
  button_label: string;
  action: string;
  level: string;
}

/**
 * Tracks when a user interacts with the navigation bar
 * @param event - The navigation bar interaction event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.NAVIGATION_BAR_INTERACTION in types.ts
 */
export function trackNavigationBarInteraction(event: NavigationBarInteractionEvent): void {
  const unique_id = 'navigation_bar_interaction';

  trackEvent(Track.WebActionTriggered, {
    unique_id,
    item_details: {
      [unique_id]: {
        button_label: event.button_label,
        action: event.action,
        level: event.level,
      },
    },
  });
}

export interface EnrollmentCtaTappedEvent {
  label: string;
  item_value: string;
  module: {
    module_title: string;
    module_name: string;
    module_index: string;
    module_type: string;
  };
  cta_type: string;
  button_location: string;
}

/**
 * Tracks when a user taps an enrollment CTA
 * @param event - The enrollment CTA event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED in types.ts
 */
export function trackEnrollmentCtaTapped(event: EnrollmentCtaTappedEvent): void {
  const unique_id = 'enrollment_cta_tapped';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    item_details: {
      [unique_id]: {
        module: event.module,
        cta_type: event.cta_type,
        button_location: event.button_location,
      },
    },
  });
}

export interface InternalLinkTappedEvent {
  label: string;
  item_value: string;
  module: {
    module_title: string;
    module_name: string;
    module_index: string;
    module_type: string;
  };
  link_type: string;
}

/**
 * Tracks when a user taps an internal link
 * @param event - The internal link event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.INTERNAL_LINK_TAPPED in types.ts
 */
export function trackInternalLinkTapped(event: InternalLinkTappedEvent): void {
  const unique_id = 'internal_link_tapped';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_LINK,
    item_details: {
      [unique_id]: {
        module: event.module,
        link_type: event.link_type,
      },
    },
  });
}

export interface SecondaryCtaClickedEvent {
  label: string;
  item_value: string;
  cta_index: string;
  module: {
    module_title: string;
    module_name: string;
    module_index: string;
    module_type: string;
  };
}

/**
 * Tracks when a user clicks a secondary CTA
 * @param event - The secondary CTA event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED in types.ts
 */
export function trackSecondaryCtaClicked(event: SecondaryCtaClickedEvent): void {
  const unique_id = 'secondary_cta_clicked';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    item_details: {
      [unique_id]: {
        cta_index: event.cta_index,
        module: event.module,
      },
    },
  });
}

export interface BlogQuizEvent {
  quizId: string;
  actionDetail: string;
  card: string;
  responses: string[];
}

/**
 * Tracks blog quiz interactions
 * @param event - The blog quiz event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.BLOG_QUIZ in types.ts
 */
export function trackBlogQuiz(event: BlogQuizEvent): void {
  const unique_id = 'blog_quiz';

  trackEvent(Track.WebItemViewed, {
    unique_id,
    label: event.quizId,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
    item_value: event.actionDetail,
    item_details: {
      [unique_id]: {
        card: event.card,
        responses: event.responses,
      },
    },
  });
}

export interface EngagementLinkEvent {
  label: string;
  item_value: string;
  module: {
    module_title: string;
    module_name: string;
    module_index: string;
    module_type: string;
  };
  cta_type: string;
}

/**
 * Tracks when a user taps an engagement link
 * @param event - The engagement link event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.ENGAGEMENT_LINK_TAPPED in types.ts
 */
export function trackEngagementLink(event: EngagementLinkEvent): void {
  const unique_id = 'engagement_link';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_LINK,
    item_details: {
      [unique_id]: {
        module: event.module,
        cta_type: event.cta_type,
      },
    },
  });
}

export interface PromoCodeEnteredEvent {
  label: string;
  status: string;
  promo_code_entered: string;
  button_location: string;
}

/**
 * Tracks when a user enters a promo code
 * @param event - The promo code event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.PROMO_CODE_ENTERED in types.ts
 */
export function trackPromoCodeEntered(event: PromoCodeEnteredEvent): void {
  const unique_id = 'promo_code_entered';

  trackEvent(Track.WebFieldFilled, {
    unique_id,
    label: event.label,
    item_details: {
      [unique_id]: {
        status: event.status,
        promo_code_entered: event.promo_code_entered,
        button_location: event.button_location,
      },
    },
  });
}

export interface FeatureBannerCtaClickedEvent {
  label: string;
  item_value: string;
}

/**
 * Tracks when a user clicks a feature banner CTA
 * @param event - The feature banner CTA event data
 * @see LEAD_GENERATION_INTERACTION_TYPES.FEATURE_BANNER_CTA_CLICKED in types.ts
 */
export function trackFeatureBannerCtaClicked(event: FeatureBannerCtaClickedEvent): void {
  const unique_id = 'feature_banner_cta_clicked';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

// PreEnrollment GA4 Events

export interface FaqArrowClickedEvent {
  faq_question: string;
}

/**
 * Tracks when a user clicks a FAQ arrow
 * @param event - The FAQ arrow event data
 * @see ANALYTICS_EVENTS.FAQ_ARROW_CLICKED in types.ts
 * @see FAQArrowClickedProps in types.ts
 */
export function trackFaqArrowClicked(event: FaqArrowClickedEvent): void {
  const unique_id = 'faq_arrow_clicked';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.faq_question,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

/**
 * Tracks when a user clicks a table of contents toggle
 * @param event - The table of contents toggle event data
 * @see TocToggleClickedProps in types.ts
 */
export function trackTocToggleClicked(event: TocToggleClickedEvent): void {
  const unique_id = 'toc_toggle_clicked';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.action,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface ExpandableCardItemToggledEvent {
  action: 'open' | 'close';
}

/**
 * Tracks when a user toggles an expandable card item
 * @param event - The expandable card item event data
 * @see ExpandableCardItemToggledEvent in types.ts
 */
export function trackExpandableCardItemToggled(event: ExpandableCardItemToggledEvent): void {
  const unique_id = 'expandable_card_item_toggled';
  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.action,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

/**
 * Tracks when a user clicks a table of contents anchor
 * @param event - The table of contents anchor event data
 * @see TocAnchorClickedEvent in types.ts
 */
export function trackTocAnchorClicked(event: TocAnchorClickedEvent): void {
  const unique_id = 'toc_anchor_clicked';

  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.anchor_label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface VisitorIdSetEvent {
  source: string;
}

/**
 * Tracks when a visitor ID is set
 * @param event - The visitor ID event data
 * @see ANALYTICS_EVENTS.VISITOR_ID_SET in types.ts
 * @see VisitorIDSetProps in types.ts
 */
export function trackVisitorIdSet(event: VisitorIdSetEvent): void {
  const unique_id = 'visitor_id_set';
  trackEvent(Track.WebActionTriggered, {
    unique_id,
    item_details: {
      [unique_id]: {
        source: event.source,
      },
    },
  });
}

export interface SliderBarClickedEvent {
  label: string;
}

/**
 * Tracks when a user clicks a slider bar
 * @param event - The slider bar event data
 * @see ANALYTICS_EVENTS.SLIDER_BAR_CLICKED in types.ts
 */
export function trackSliderBarClicked(event: SliderBarClickedEvent): void {
  const unique_id = 'slider_bar_clicked';
  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface BlogSearchSubmittedEvent {
  search_term: string;
  search_location: string;
}

/**
 * Tracks when a user submits a blog search
 * @param event - The blog search event data
 */
export function trackBlogSearchSubmitted(event: BlogSearchSubmittedEvent): void {
  const unique_id = 'blog_search_submitted';
  trackEvent(Track.WebFormSubmitted, {
    unique_id,
    fields: [
      { field_name: 'search_term', field_value: event.search_term },
      { field_name: 'search_location', field_value: event.search_location },
    ],
  });
}

export interface LandingPageEmailFilledEvent {
  email: string;
  button_location: string;
}

/**
 * Tracks when a user fills in an email on a landing page
 * @param event - The landing page email event data
 * @see ANALYTICS_EVENTS.LANDING_PAGE_EMAIL_FILLED in types.ts
 */
export function trackLandingPageEmailFilled(event: LandingPageEmailFilledEvent): void {
  const unique_id = 'landing_page_email_filled';
  trackEvent(Track.WebFieldFilled, {
    unique_id,
    value: event.email,
    item_details: {
      [unique_id]: {
        button_location: event.button_location,
      },
    },
  });
}

export interface LoginLinkTappedEvent {
  cta_copy: string;
  lp_type: string;
}

/**
 * Tracks when a user taps a login link
 * @param event - The login link event data
 * @see ANALYTICS_EVENTS.LOGIN_LINK_TAPPED in types.ts
 * @see LoginLinkTappedProps in types.ts
 */
export function trackLoginLinkTapped(event: LoginLinkTappedEvent): void {
  const unique_id = 'login_link_tapped';
  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.cta_copy,
    element_type: ElementType.ELEMENT_TYPE_LINK,
    item_details: {
      [unique_id]: {
        lp_type: event.lp_type,
      },
    },
  });
}

export interface ModuleViewedEvent {
  module_title: string;
  module_name: string;
  module_index: string;
  module_type: string;
  cta_type: string;
}

/**
 * Tracks when a module is viewed
 * @param event - The module viewed event data
 * @see ANALYTICS_EVENTS.MODULE_VIEWED in types.ts
 */
export function trackModuleViewed(event: ModuleViewedEvent): void {
  const unique_id = 'module_viewed';
  trackEvent(Track.WebItemViewed, {
    unique_id,
    element_type: ElementType.ELEMENT_TYPE_SECTION,
    label: event.module_title,
    item_value: event.cta_type,
    item_details: {
      [unique_id]: {
        // Keyed by a name computed at runtime, so the index signature is the contract.
        // oxlint-disable-next-line anti-slop/no-known-value-widening
        module: {
          module_type: event.module_type,
          module_name: event.module_name,
          module_index: event.module_index,
          module_title: event.module_title,
        } as Record<string, string>,
      },
    },
  });
}

export interface AccordionClickedEvent {
  label: string;
}

/**
 * Tracks when a user taps an accordion
 * @param event - The accordion event data
 * @see ANALYTICS_EVENTS.ACCORDION_CLICKED in types.ts
 */
export function trackAccordionClicked(event: AccordionClickedEvent): void {
  const unique_id = 'accordion_click';
  trackEvent(Track.WebItemClicked, {
    unique_id,
    label: event.label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface VideoInteractionEvent {
  title: string;
  current_time: string;
  duration: string;
  percent_complete: string;
  provider: string;
  status: string;
  url: string;
}

/**
 * Tracks when a user interacts with a video
 * @param event - The video interaction event data
 * @see ANALYTICS_EVENTS.VIDEO_INTERACTION in types.ts
 */
export function trackVideoInteraction(event: VideoInteractionEvent): void {
  const unique_id = 'video_interaction';
  trackEvent(Track.WebActionTriggered, {
    unique_id,
    item_details: {
      [unique_id]: {
        video_title: event.title,
        video_current_time: event.current_time,
        video_duration: event.duration,
        video_percent: event.percent_complete,
        video_provider: event.provider,
        video_status: event.status,
        video_url: event.url,
      },
    },
  });
}

/**
 * Tracks when the floating disclaimer banner becomes visible.
 * @see ANALYTICS_EVENTS.FLOATING_DISCLAIMER_SHOWN in types.ts
 */
export function trackFloatingDisclaimerShown(): void {
  const unique_id = 'floating_disclaimer_shown';
  trackEvent(Track.WebItemViewed, {
    unique_id,
    element_type: ElementType.ELEMENT_TYPE_SECTION,
  });
}

/**
 * Tracks when the floating disclaimer banner is dismissed.
 */
export function trackFloatingDisclaimerDismissed(): void {
  const unique_id = 'floating_disclaimer_dismissed';
  trackEvent(Track.WebItemClicked, {
    unique_id,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

/**
 * Tracks when a user views their first page
 */
export function trackFirstPageViewed(): void {
  const unique_id = 'first_entry_point';
  trackEvent(Track.WebActionTriggered, {
    unique_id,
  });
}

/**
 * Tracks when a user scrolls a certain percentage of the page
 * @param event - The scroll depth event data
 * @see ANALYTICS_EVENTS.SCROLL_DEPTH in types.ts
 * @see ScrollDepthProps in types.ts
 */
export function trackScrollDepth(event: ScrollDepthProps): void {
  const unique_id = 'pre_enrollment_scroll_depth';
  trackEvent(Track.WebBrowserActivityTracked, {
    unique_id,
    activity_type: 'BROWSER_ACTIVITY_TYPE_SCROLL_DEPTH' as BrowserActivityType,
    activity_value: String(event.depth_percentage),
  });
}

export function trackBudgetCalculatorCalculate(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'budget_calculator_calculate',
    label: 'Calculate',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface CalculatorResetEvent {
  label: 'Reset' | 'Start Over';
}

export function trackBudgetCalculatorReset(event: CalculatorResetEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'budget_calculator_reset',
    label: event.label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export function trackBudgetCalculatorEditAmounts(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'budget_calculator_edit_amounts',
    label: 'Edit Amounts',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export function trackEmergencyFundCalculatorCalculate(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'emergency_fund_calculator_calculate',
    label: 'Calculate',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export function trackEmergencyFundCalculatorReset(event: CalculatorResetEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'emergency_fund_calculator_reset',
    label: event.label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export function trackOverdraftCalculatorCalculate(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'overdraft_calculator_calculate',
    label: 'Calculate',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

/** The overdraft calculator's only exit keeps the slider, so it is a Start Over rather than a Reset. */
export function trackOverdraftCalculatorReset(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'overdraft_calculator_reset',
    label: 'Start Over',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface OverdraftCalculatorFrequencyEvent {
  label: 'Weekly' | 'Monthly' | 'Annually';
}

/**
 * The frequency is the one input here that is not a count, so reporting it costs no financial
 * detail. The schema has no radio element type; legacy styled these as buttons anyway.
 */
export function trackOverdraftCalculatorFrequency(event: OverdraftCalculatorFrequencyEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'overdraft_calculator_frequency',
    label: event.label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface ApySavingsCalculatorCalculateEvent {
  /** The savings period selected. */
  label: string;
  /** The contribution frequency selected. */
  item_value: string;
}

/**
 * Both selections are categorical, so neither reveals an amount the way a balance or a rate would.
 * The dropdowns get no events of their own; a `<select>` would report browsing rather than intent.
 */
export function trackApySavingsCalculatorCalculate(event: ApySavingsCalculatorCalculateEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'apy_savings_calculator_calculate',
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

/** One reset control with a fixed label, so there is nothing for a `CalculatorResetEvent` to disambiguate. */
export function trackApySavingsCalculatorReset(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'apy_savings_calculator_reset',
    label: 'Reset',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface LoanCalculatorCalculateEvent {
  /** The rate type selected: an interest rate or an APR. */
  label: string;
  /** The term unit selected, and whether an extra payment was entered. */
  item_value: string;
}

/**
 * Rate type, term unit and the presence of an extra payment are categorical, so none of them
 * reveals the amount, rate or term the borrower typed.
 */
export function trackLoanCalculatorCalculate(event: LoanCalculatorCalculateEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'loan_calculator_calculate',
    label: event.label,
    item_value: event.item_value,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export function trackLoanCalculatorReset(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'loan_calculator_reset',
    label: 'Reset',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface LoanCalculatorTryExtraPaymentEvent {
  /** Whether the modelled extra is the borrower's own figure or the teaser's default share. */
  label: 'Entered' | 'Modelled';
}

/** Fires on reveal only; closing the comparison is not a second intent, and neither is a Calculate. */
export function trackLoanCalculatorTryExtraPayment(event: LoanCalculatorTryExtraPaymentEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'loan_calculator_try_extra_payment',
    label: event.label,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export interface PaycheckCalculatorCalculateEvent {
  /** The pay type selected: hourly or salary. */
  label: string;
  /** The pay frequency selected, or `Not selected`, since the frequency has no default. */
  item_value: string;
  /** Whether the press passed validation; the event schema has no boolean field to carry it. */
  item_type: 'success' | 'failure';
}

/**
 * Pay type and frequency are categorical, so neither reveals the wage, hours or salary typed.
 * Filing status and state stay out too: one discloses marital status, the other location.
 */
export function trackPaycheckCalculatorCalculate(event: PaycheckCalculatorCalculateEvent): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'paycheck_calculator_calculate',
    label: event.label,
    item_value: event.item_value,
    item_type: event.item_type,
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}

export function trackPaycheckCalculatorReset(): void {
  trackEvent(Track.WebItemClicked, {
    unique_id: 'paycheck_calculator_reset',
    label: 'Reset',
    element_type: ElementType.ELEMENT_TYPE_BUTTON,
  });
}
